<app-page>
    <div class="enroll-review-container col-12 m-auto py-5 px-3 md:px-5 lg:px-8">
        <!-- HEADER SECTION -->
        <div class="mb-5">
            <h1 class="m-0 mb-2 font-bold text-slate-900 text-4xl">Complete order</h1>
            <p class="m-0 text-slate-600 text-base">To enroll please review the information below and click enroll.</p>
        </div>

        <!-- ERROR MESSAGE -->
        <div class="w-full mt-2 mb-4" *ngIf="enrollmentError">
            <p-message severity="error" [text]="enrollmentError" class="plain-message" icon="pi pi-ban"
                data-auto-id="enrollment-error-message" styleClass="w-full justify-content-start" closable></p-message>
        </div>

        <!-- MAIN GRID CONTAINER -->
        <div class="flex flex-column gap-2 lg:gap-5 lg:flex-row p-0" *ngIf="contractData && !enrollmentSuccess">

            <!-- LEFT COLUMN (Contact information & Billing method) -->
            <div class="flex-1 flex flex-column p-0">

                <!-- CONTACT INFORMATION CARD -->
                <div class="p-4 bg-white border-round-xl h-full"
                    *ngIf="contractData?.baseContract?.primaryCustomer && contractData.baseContract.propertyAddress">
                    <h2 class="m-0 mb-4 font-bold text-slate-950 text-xl">Contact
                        information</h2>

                    <div class="flex flex-column gap-4">
                        <!-- Customer Name -->
                        <div class="flex flex-column">
                            <span class="text-slate-950 font-bold">Customer name</span>
                            <span class="text-slate-700 capitalize text-sm py-1">
                                {{ contractData.baseContract.primaryCustomer.firstName }} {{
                                contractData.baseContract.primaryCustomer.lastName }}
                            </span>
                        </div>

                        <!-- Covered Property -->
                        <div class="flex flex-column">
                            <span class="text-slate-950 font-bold">Covered property</span>
                            <span class="text-slate-700 text-sm py-1">
                                {{ contractData.baseContract.propertyAddress.addressLine1 }}, {{
                                contractData.baseContract.propertyAddress.city }}, {{
                                contractData.baseContract.propertyAddress.state }} {{
                                contractData.baseContract.propertyAddress.zip }}
                            </span>
                        </div>

                        <!-- Phone Numbers -->
                        <div class="flex flex-column">
                            <span class="flex align-items-center gap-2 font-bold text-slate-950">
                                <i class="pi pi-phone"></i> Phone numbers
                            </span>
                            <ng-container *ngIf="contractData.contact?.phoneList as phoneList">
                                <div *ngFor="let phone of phoneList" class="pb-1">
                                    <span *ngIf="phone.phoneType && phone.phoneNumber" class="text-slate-700 text-sm">
                                        {{ (phone.phoneType | phoneType ) | titlecase }} : {{ phone.phoneNumber | phone
                                        }}
                                    </span>
                                </div>
                            </ng-container>

                        </div>

                        <!-- Email -->
                        <div *ngIf="contractData.baseContract.primaryCustomerEmail" class="flex flex-column">
                            <span class="flex align-items-center gap-2 text-slate-950 font-bold">
                                <i class="pi pi-envelope"></i> Email
                            </span>
                            <a class="underline text-blue-500 text-sm py-1">
                                {{ contractData.baseContract.primaryCustomerEmail }}
                            </a>
                        </div>
                    </div>

                    <div class="border-bottom-1 border-slate-200 p-2"></div>

                    <div class="flex flex-column">
                        <div class="flex align-items-center justify-content-between mt-3 mb-2">
                            <h2 class="m-0 font-bold text-slate-950 text-xl">Billing method</h2>

                            <!-- REQUIRED VALIDATION BADGE -->
                            <div *ngIf="isPaymentRequiredError && !hasSavedPaymentInfo && !addNewCard && !addNewBankAccount"
                                class="border-round-md bg-orange-100 text-xs text-orange-900 px-2 py-1 flex align-items-center gap-1"
                                data-auto-id="enroll-review-payment-required-badge">
                                <i class="pi pi-exclamation-triangle text-xs"></i> Required
                            </div>
                        </div>
                        <p class="m-0 text-slate-700 text-sm mb-3">Billing frequency: Monthly</p>

                        <!-- SAVED PAYMENT METHOD CARD DISPLAY -->
                        <div class="p-3 border-1 border-slate-200 border-round-xl mt-2 mb-3 flex align-items-center justify-content-between"
                            *ngIf="hasSavedPaymentInfo && !addNewCard && !addNewBankAccount">

                            <!-- CREDIT CARD DISPLAY -->
                            <div class="flex align-items-center gap-2" *ngIf="hasSavedCreditCard">
                                <img class="h-2rem" [src]="getCardTypeImage()" alt="Credit card logo" />
                                <span class="text-slate-700 font-medium">{{ getSavedCardLastFour() }}</span>
                            </div>

                            <!-- BANK ACCOUNT DISPLAY -->
                            <div class="flex align-items-center gap-2" *ngIf="hasSavedBankAccount">
                                <i class="pi pi-building-columns text-xl text-slate-700"></i>
                                <span class="text-slate-700 font-medium">{{ getSavedBankAccountLastFour() }}</span>
                            </div>

                            <p-button [link]="true" (click)="editSavedPayment()"
                                data-auto-id="enroll-review-edit-payment-button"
                                styleClass="p-0 text-blue-600 font-semibold text-sm">
                                <span class="underline">Edit</span>
                            </p-button>
                        </div>

                        <!-- INLINE CREDIT CARD FORM SECTION (ADD OR EDIT) -->
                        <div *ngIf="addNewCard"
                            class="p-4 bg-slate-50 border-1 border-slate-200 border-round-xl my-2 flex flex-column gap-3"
                            data-auto-id="enroll-review-payment-form-section">
                            <h3 class="m-0 font-bold text-slate-900 text-lg">
                                {{ hasSavedCreditCard ? 'Edit Credit Card' : 'Add Credit Card' }}
                            </h3>

                            <app-add-payment [paymentTypeSelected]="paymentTypeCreditCard"
                                data-auto-id="enroll-review-add-payment-form">
                            </app-add-payment>

                            <div class="flex justify-content-end gap-2 mt-2">
                                <p-button label="Cancel" severity="secondary" rounded="true" outlined="true"
                                    (click)="cancelAddingPayment()" data-auto-id="enroll-review-cancel-payment-button"
                                    styleClass="w-full " class="w-full"></p-button>
                                <p-button label="Save" severity="primary" rounded="true"
                                    (click)="savePaymentInformation()" data-auto-id="enroll-review-save-payment-button"
                                    styleClass="w-full " class="w-full"></p-button>
                            </div>
                        </div>

                        <!-- INLINE BANK ACCOUNT FORM SECTION (ADD OR EDIT) -->
                        <div *ngIf="addNewBankAccount"
                            class="p-4 bg-slate-50 border-1 border-slate-200 border-round-xl my-2 flex flex-column gap-3"
                            data-auto-id="enroll-review-payment-form-section">
                            <h3 class="m-0 font-bold text-slate-900 text-lg">
                                {{ hasSavedBankAccount ? 'Edit Bank Account' : 'Add Bank Account' }}
                            </h3>

                            <app-add-payment [paymentTypeSelected]="paymentTypeBankAccount"
                                data-auto-id="enroll-review-add-payment-form"></app-add-payment>

                            <div class="flex justify-content-end gap-2 mt-2">
                                <p-button label="Cancel" severity="secondary" rounded="true" outlined="true"
                                    (click)="cancelAddingPayment()" data-auto-id="enroll-review-cancel-payment-button"
                                    styleClass="w-full " class="w-full"></p-button>
                                <p-button label="Save" severity="primary" rounded="true"
                                    (click)="savePaymentInformation()" data-auto-id="enroll-review-save-payment-button"
                                    styleClass="w-full " class="w-full"></p-button>
                            </div>
                        </div>

                        <!-- ACTION BUTTONS -->
                        <div class="flex flex-column gap-2 mt-2" *ngIf="!addNewCard && !addNewBankAccount">
                            <app-add-new-credit-card-action *ngIf="!newCardAdded" [addOrEditCard]="addOrEditCard"
                                data-auto-id="enroll-review-add-credit-card-button"></app-add-new-credit-card-action>

                            <app-add-new-bank-account-action *ngIf="!newBankAccountAdded"
                                [addOrEditBankAccount]="addOrEditBankAccount"
                                data-auto-id="enroll-review-add-bank-account-button"></app-add-new-bank-account-action>
                        </div>
                    </div>
                </div>
            </div>

            <!-- RIGHT COLUMN (Plan details, summary card & CTAs) -->
            <div class="flex-1 flex flex-column p-0">
                <div class="p-4 bg-white border-round-xl flex flex-column gap-2 h-full">

                    <!-- PRODUCT DESCRIPTION SECTION -->
                    <div class="flex align-items-start gap-2">
                        <div class="flex justify-content-center align-items-center bg-white">
                            <img class="w-3rem h-3rem" src="assets/icons/shield-check.png"
                                alt="Electronics Essentials Plan">
                        </div>
                        <div>
                            <h2 class="m-0 font-bold text-slate-950 text-lg">Electronics Essentials Plan</h2>
                            <p class="m-0 text-slate-500 text-xs mt-1 leading-relaxed">Cinch helps you protect your
                                expensive electronics.</p>
                            <button type="button" (click)="openSampleContract()"
                                data-auto-id="view-annual-sample-contract-link"
                                class="sample-contract-link text-blue-500 underline text-sm cursor-pointer border-none bg-transparent p-0 text-left">
                                View annual sample contract
                            </button>
                        </div>
                    </div>

                    <!-- DEDUCTIBLE -->
                    <div class="flex align-items-center text-slate-600 text-sm py-2 border-bottom-1 border-slate-200"
                        *ngIf="productDetails?.productInfo?.deductible">
                        <span>Deductible:</span>
                        <span class="ml-2">{{ productDetails?.productInfo?.deductible | currency }}</span>
                    </div>

                    <!-- PRICING TABLE -->
                    <div class="flex flex-column gap-3 py-2">
                        <div class="flex justify-content-between align-items-center text-slate-600 text-sm">
                            <span>Monthly payment</span>
                            <span class="font-bold text-slate-800" data-auto-id="enrollment-monthly-cost">{{ monthlyCost
                                | currency }}</span>
                        </div>
                        <div class="flex justify-content-between align-items-center text-slate-600 text-sm">
                            <span>Taxes</span>
                            <span class="font-bold text-slate-800" data-auto-id="enrollment-tax">{{ taxAmount | currency
                                }}</span>
                        </div>
                    </div>

                    <!-- TOTAL -->
                    <div class="flex justify-content-end border-bottom-1 border-slate-200 pb-2">
                        <span class="font-bold text-slate-800 text-base mb-2">Total</span>
                        <span class="font-bold text-blue-600 text-base ml-2 mb-2"
                            data-auto-id="enrollment-total-monthly-cost">{{ totalMonthlyCost | currency
                            }}</span>
                    </div>

                    <!-- REQUIRED AUTHORIZATION -->
                    <div class="py-2" [formGroup]="termsForm">
                        <p class="p-1 border-round-md bg-orange-100 text-xs text-orange-900 mb-1 mt-0 w-fit"
                            *ngIf="termsForm.controls['agreeToTerms'].invalid && (termsForm.controls['agreeToTerms'].dirty || termsForm.controls['agreeToTerms'].touched)">
                            <i class="pi pi-exclamation-triangle text-xs"></i> Required
                        </p>
                        <div class="flex flex-column gap-3">
                            <div class="flex align-items-start gap-3">
                                <p-checkbox formControlName="agreeToTerms" [binary]="true" inputId="agreeToTerms"
                                    data-auto-id="payment-terms-enrollment-checkbox" [disabled]="!paymentTermsVerbiage"
                                    [styleClass]="termsForm.controls['agreeToTerms'].invalid && (termsForm.controls['agreeToTerms'].dirty || termsForm.controls['agreeToTerms'].touched) ? 'ng-invalid ng-dirty' : ''"></p-checkbox>
                                <div class="flex-1 w-full">
                                    <label *ngIf="paymentTermsVerbiage" for="agreeToTerms"
                                        class="text-xs text-slate-600 leading-relaxed cursor-pointer select-none"
                                        [innerHTML]="paymentTermsVerbiage">
                                    </label>
                                    <div *ngIf="!paymentTermsVerbiage" class="flex flex-column gap-2 w-full pt-1">
                                        <p-skeleton width="100%" height="12px"></p-skeleton>
                                        <p-skeleton width="95%" height="12px"></p-skeleton>
                                        <p-skeleton width="60%" height="12px"></p-skeleton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ACTION BUTTONS -->
                    <div class="flex flex-wrap justify-content-center align-items-center w-full gap-2 mb-3 mt-2">
                        <p-button class="flex-1  border-round-3xl font-semibold cursor-pointer" styleClass="w-full"
                            data-auto-id="back-to-offer" (click)="goBack()" severity="secondary" rounded="true"
                            outlined="true">
                            Back
                        </p-button>
                        <p-button class="flex-1  border-round-3xl font-semibold cursor-pointer" styleClass="w-full"
                            data-auto-id="enroll-now" [disabled]="isSubmitting || !paymentTermsVerbiage"
                            (click)="confirmEnrollment()" severity="primary" rounded="true">
                            <span *ngIf="!isSubmitting">Buy now</span>
                            <span *ngIf="isSubmitting"><i class="pi pi-spin pi-spinner mr-2"></i>Processing</span>
                        </p-button>
                    </div>

                </div>
            </div>

        </div>

        <div class="pt-4 lg:pt-6 text-slate-600">
            <p class="m-0 mt-2">{{ content.tnc.description }}</p>
        </div>
    </div>
</app-page>